'use client';
import type {
  AIChatConfig,
  DealerWeb2TextStatus,
  Web2TextConfig,
} from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { toTitleCase, trackEvent } from '@dealerportal/utilities';
import Fab from '@mui/material/Fab';
import classNames from 'classnames';
import type { FormikProps, FormikValues } from 'formik';
import { useCtaColor } from 'libs/react-helpers/src/lib/hooks/useCtaColor';
import {
  BetterInput,
  type BetterInputProps,
} from 'libs/ui-components/src/lib/BetterInput/index';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import ReCaptcha from 'react-google-recaptcha';
import FloatingButtonClosed from './FloatingButtonClosed';
import FloatingButtonOpen from './FloatingButtonOpen';
import FloatingOpenContainer from './FloatingContainer';
import { Prompt } from './FloatingPrompt';
import { resolveChatColor } from './chatColor';

/* eslint-disable-next-line */
export interface ChatProps {
  DealerStatus?: DealerWeb2TextStatus;
  W2TConfig: Web2TextConfig;
  AIChatConfig: AIChatConfig;
  Recaptcha: {
    Enabled: boolean;
    SiteKey?: string;
  };
  FallbackStorePhone: string;
}
export const ChatContext = React.createContext<Partial<ChatProps>>({});
export function Chat(props: ChatProps) {
  const t = useTranslations('ui');
  const [isOpen, setIsOpen] = useState(false);
  const [isPopupPromptOpen, setIsPopupPromptOpen] = useState(false);
  const [hasUserInitiatedChat, setHasUserInitiatedChat] = useState(false);

  const toggleWeb2Text = () => {
    trackEvent('CHAT_OPENED');
    setIsOpen((prev) => !prev);
    setHasUserInitiatedChat(true);
  };

  useEffect(() => {
    const showPrompt = !isOpen && !isPopupPromptOpen && !hasUserInitiatedChat;

    const timeout = setTimeout(() => {
      if (showPrompt) {
        setIsPopupPromptOpen(true);
        setHasUserInitiatedChat(true);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [isOpen, isPopupPromptOpen, hasUserInitiatedChat]);

  const { chatColor: w2tChatColor, ctaColor } = useCtaColor(
    props.W2TConfig?.ChatColor?.replace('bg', '-')
  );

  const chatColor = resolveChatColor(
    props?.W2TConfig?.ChatColor,
    props?.W2TConfig?.CustomColor,
    w2tChatColor,
    ctaColor
  );

  return (
    <ChatContext.Provider value={props}>
      {chatColor && (
        <div
          className={classNames(
            'ui-fixed ui-z-[9997] ui-bottom-5',
            props?.W2TConfig?.ChatPosition === 'right'
              ? 'ui-right-4'
              : 'ui-left-4'
          )}
          aria-label={
            props?.W2TConfig?.ChatCallToActionLabel ?? t('chatButton')
          }
        >
          <div
            className={classNames(
              'ui-flex ui-flex-col-reverse ui-gap-x-8 ui-h-0',
              props?.W2TConfig?.ChatPosition === 'right' ? 'ui-items-end' : ''
            )}
          >
            <Fab
              aria-label={t('likeButton')}
              variant={'extended'}
              size="small"
              className={
                'ui-relative !ui-w-fit ui-z-[1100] !ui-min-h-fit !ui-transition-transform !ui-duration-300 !ui-h-auto ui-mt-3 !ui-p-0 !ui-rounded-3xl !ui-text-sm !ui-font-medium !ui-normal-case !ui-text-white !ui-shadow-2xl'
              }
              style={{
                transitionTimingFunction:
                  'cubic-bezier(0.4, 0, 0.2, 1) !important',
                backgroundColor: chatColor,
              }}
              onClick={() => {
                toggleWeb2Text();
                setIsPopupPromptOpen(false);
              }}
            >
              {isOpen ? (
                <FloatingButtonOpen isOpen={isOpen} />
              ) : (
                <FloatingButtonClosed
                  isOpen={isOpen}
                  chatCallToActionLabel={
                    props?.W2TConfig?.ChatCallToActionLabel
                  }
                />
              )}
            </Fab>
            {isPopupPromptOpen && (
              // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
              <div
                className={`ui-mb-4 ${props?.W2TConfig?.ChatPosition === 'right' ? 'ui-mr-4' : 'ui-ml-4'}`}
                onClick={() => {
                  toggleWeb2Text();
                  setIsPopupPromptOpen(false);
                }}
              >
                <Prompt
                  message={props?.W2TConfig?.ChatPromptMessage ?? ''}
                  position={props?.W2TConfig?.ChatPosition ?? 'right'}
                />
              </div>
            )}
            <FloatingOpenContainer
              isOpen={isOpen}
              toggleWeb2Text={() => {
                toggleWeb2Text();
              }}
              web2TextConfig={props.W2TConfig}
              aiChatConfig={props.AIChatConfig}
              recaptcha={props.Recaptcha}
              fallbackStorePhone={props.FallbackStorePhone}
              chatBgColor={chatColor}
            />
          </div>
        </div>
      )}
    </ChatContext.Provider>
  );
}

type CustomFormProps<X, T extends FormikProps<X>> = {
  form: T;
  submit?: boolean;
  formProps: {
    [key in keyof T['values']]?: BetterInputProps;
  };
  recaptcha?: {
    Enabled: boolean;
    SiteKey?: string;
  };
};
export const CustomForm = <X extends FormikValues, T extends FormikProps<X>>({
  form,
  formProps,
  recaptcha,
}: CustomFormProps<X, T>): React.ReactElement => {
  const t = useTranslations();
  const recaptchaRef = useRef<ReCaptcha>(null);
  const recaptchaEnabled = recaptcha?.Enabled && recaptcha?.SiteKey != null;
  const ignoreInputFields = ['recaptcha', 'closeOldLead', 'locationId'];

  if (
    form?.isSubmitting ||
    (form?.submitCount &&
      // A settled submission (status set) shows the read-only summary; a
      // guarded early-return (e.g. expired recaptcha) leaves status unset and
      // must fall through to the editable form so the user can retry.
      form?.status != null &&
      Object.keys(form?.errors ?? {}).length === 0)
  ) {
    const labelByKey: Record<string, string> = {
      fullName: t('chat.fullName'),
      countryCode: t('chat.countryCode'),
      phoneNumber: t('chat.phoneNumber'),
      message: t('chat.message'),
      location: t('chat.chooseLocation'),
      contactMethod: t('chat.preferredContactMethod'),
    };
    const contactMethodLabelByValue: Record<string, string> = {
      text: t('chat.textMessage'),
      phone: t('chat.phoneCall'),
      whatsapp: t('chat.whatsAppMessage'),
    };
    return (
      <div className="ui-flex ui-flex-col ui-gap-y-2 ui-max-w-[220px]">
        {Object.entries(form?.values)
          ?.filter(([key]) => !ignoreInputFields.includes(key))
          ?.map(([key, value]) => (
            <span key={key} className="ui-text-sm ui-break-words">
              <span className="ui-font-semibold">
                {(labelByKey[key] ?? toTitleCase(key)).trim()}:
              </span>{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    key === 'contactMethod'
                      ? (contactMethodLabelByValue[String(value)] ??
                        String(value))
                      : String(value),
                }}
              />
            </span>
          ))}
      </div>
    );
  }

  return (
    <div>
      {Object.entries(formProps).map(([formKey, formFieldProps]) => {
        return (
          <BetterInput
            key={formKey}
            {...(formFieldProps ?? {})}
            inputProps={{
              id: formKey,
              name: formKey,
              ...(formFieldProps?.inputProps ?? {}),
            }}
          />
        );
      })}
      {recaptchaEnabled && (
        <ReCaptcha
          ref={recaptchaRef}
          sitekey={recaptcha.SiteKey!}
          className="g-recaptcha -ui-ml-5"
          onChange={(value) => {
            form.setFieldValue('recaptcha', value);
          }}
          onExpired={() => {
            form.setFieldValue('recaptcha', '');
          }}
        />
      )}
    </div>
  );
};

export default Chat;
